import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import Loading from "../../../../utils/LoadingIndicator/Loading";
import Dashboard from "../../Dashboard";
import useCategory from "../../../../features/adminDashboard/category/useCategory";
import DeleteCategoryModal from "../../../../features/adminDashboard/category/DeleteCategoryModal";
import ListCategories from "../../../../features/adminDashboard/category/ListCategories";
import CreateCategory from "../../../../features/adminDashboard/category/CreateCategory";

const Categories = () => {
  const [activeTab, setActiveTab] = useState("view");
  const [editingCategory, setEditingCategory] = useState(null);
  const [name, setName] = useState("");
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    category: null,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Use existing useCategory hook
  const {
    categories,
    isGettingCategories,
    errorGettingCategories,
    createCategoryFn,
    isCreatingCategory,
    updateCategoryFn,
    isUpdatingCategory,
    deleteCategoryFn,
    isDeletingCategory,
  } = useCategory();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Category name is required");
      return;
    }

    if (editingCategory) {
      updateCategoryFn({
        categoryId: editingCategory._id,
        category: name,
      });
      resetForm();
      setActiveTab("view");
    } else {
      createCategoryFn(name);
      resetForm();
      setActiveTab("view");
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    // setFormData({
    //   name: category.name,
    //   description: category.description || "",
    // });
    setActiveTab("create");
  };

  const handleDelete = (category) => {
    setDeleteModal({ isOpen: true, category });
  };

  const confirmDelete = () => {
    if (deleteModal.category) {
      deleteCategoryFn(deleteModal.category._id);
      setDeleteModal({ isOpen: false, category: null });
    }
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, category: null });
  };

  const resetForm = () => {
    setName("");
    setEditingCategory(null);
  };

  const handleCancel = () => {
    resetForm();
    setActiveTab("view");
  };

  // if (isGettingCategories) return <Loading />;

  if (errorGettingCategories) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <h2 className="text-red-500 text-xl">
          {errorGettingCategories || "Failed to load categories"}
        </h2>
      </div>
    );
  }

  return (
    <Dashboard
      title="Category Management"
      description="Create and manage product categories"
    >
      {isGettingCategories ? (
        <Loading />
      ) : (
        <div className="w-full h-full px-4 pb-8">
          {/* Tab Navigation */}
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6">
            <button
              onClick={() => setActiveTab("view")}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === "view"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              View Categories
            </button>
            <button
              onClick={() => setActiveTab("create")}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === "create"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {editingCategory ? "Edit Category" : "Create Category"}
            </button>
          </div>

          {/* Content */}
          {activeTab === "view" ? (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  All Categories ({categories.length})
                </h2>
                <button
                  onClick={() => setActiveTab("create")}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                >
                  Add New Category
                </button>
              </div>

              {categories.length === 0 ? (
                <div className="text-center py-12">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No categories
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Get started by creating a new category.
                  </p>
                </div>
              ) : (
                <ListCategories
                  categories={categories}
                  handleDelete={handleDelete}
                  handleEdit={handleEdit}
                />
              )}
            </div>
          ) : (
            <CreateCategory
              editingCategory={editingCategory}
              handleCancel={handleCancel}
              handleSubmit={handleSubmit}
              isCreatingCategory={isCreatingCategory}
              isUpdatingCategory={isUpdatingCategory}
              name={name}
              onSetName={setName}
            />
          )}

          {/* Delete Confirmation Modal */}
          <DeleteCategoryModal
            isOpen={deleteModal.isOpen}
            onClose={closeDeleteModal}
            onConfirm={confirmDelete}
            categoryName={deleteModal.category?.name || ""}
            isDeleting={isDeletingCategory}
          />
        </div>
      )}
    </Dashboard>
  );
};

export default Categories;
